/**
 * React Hook for GPS Tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { trackingService, TrackingPoint, TrackingMetrics } from '@/services/tracking-service';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface UseTrackingReturn {
  isTracking: boolean;
  isPaused: boolean;
  points: TrackingPoint[];
  metrics: TrackingMetrics;
  startTracking: () => Promise<void>;
  pauseTracking: () => Promise<void>;
  resumeTracking: () => Promise<void>;
  stopTracking: () => Promise<{ points: TrackingPoint[]; metrics: TrackingMetrics }>;
  exportGPX: () => Promise<string>;
  exportFIT: () => Promise<Blob>;
}

export function useTracking(): UseTrackingReturn {
  const { toast } = useToast();
  const [isTracking, setIsTracking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [points, setPoints] = useState<TrackingPoint[]>([]);
  const [metrics, setMetrics] = useState<TrackingMetrics>({
    p95Error: 0,
    gapPercentage: 0,
    batteryDrain: 0,
    reconnectionTime: 0
  });

  const startTracking = useCallback(async () => {
    try {
      const sessionId = `run_${Date.now()}`;
      await trackingService.startTracking(sessionId);
      setIsTracking(true);
      setIsPaused(false);
      
      toast({
        title: 'Tracking Started',
        description: 'Recording your run with high accuracy GPS',
      });
    } catch (error) {
      logger.error('Failed to start tracking', error, { component: 'useTracking' });
      toast({
        variant: 'destructive',
        title: 'Tracking Error',
        description: 'Failed to start GPS tracking. Please check permissions.',
      });
    }
  }, [toast]);

  const pauseTracking = useCallback(async () => {
    try {
      await trackingService.pauseTracking();
      setIsPaused(true);

      // ✅ Atualizar pontos e métricas imediatamente a partir do service
      setPoints(trackingService.getCurrentPoints() ?? []);
      setMetrics(trackingService.getMetrics() ?? { p95Error: 0, gapPercentage: 0, batteryDrain: 0, reconnectionTime: 0 });
      
      toast({
        title: 'Paused',
        description: 'Tracking paused',
      });
    } catch (error) {
      logger.error('Failed to pause tracking', error, { component: 'useTracking' });
    }
  }, [toast]);

  const resumeTracking = useCallback(async () => {
    try {
      await trackingService.resumeTracking();
      setIsPaused(false);
      
      toast({
        title: 'Resumed',
        description: 'Tracking resumed',
      });
    } catch (error) {
      logger.error('Failed to resume tracking', error, { component: 'useTracking' });
    }
  }, [toast]);

  const stopTracking = useCallback(async () => {
    try {
      const result = await trackingService.stopTracking();
      setIsTracking(false);
      setIsPaused(false);
      
      return result;
    } catch (error) {
      logger.error('Failed to stop tracking', error, { component: 'useTracking' });
      throw error;
    }
  }, []);

  const exportGPX = useCallback(async () => {
    return trackingService.exportGPX();
  }, []);

  const exportFIT = useCallback(async () => {
    return trackingService.exportFIT();
  }, []);

  // Poll for updates when tracking - optimized for Strava-class performance
  useEffect(() => {
    if (!isTracking || isPaused) return;

    // ✅ CRITICAL: Start with current points from service (may be hydrated from backup)
    const currentPoints = trackingService.getCurrentPoints() ?? [];
    if (currentPoints.length > 0) {
      setPoints(currentPoints);
      logger.info('useTracking: Initialized with existing points', { count: currentPoints.length });
    }

    // Subscribe to instant location updates for real-time UI
    const unsubscribe = trackingService.onLocationUpdate((point) => {
      setPoints(prev => [...prev, point]);
    });

    // ⚡ Metrics update at 250ms for ultra-responsive UI
    const metricsInterval = setInterval(() => {
      setMetrics(trackingService.getMetrics() ?? { p95Error: 0, gapPercentage: 0, batteryDrain: 0, reconnectionTime: 0 });
      
      // ✅ SYNC CHECK: If service has more points than hook, sync them
      const servicePoints = trackingService.getCurrentPoints() ?? [];
      setPoints(prev => {
        if (servicePoints.length > prev.length) {
          return servicePoints;
        }
        return prev;
      });
    }, 250); // ⚡ 250ms for ultra-responsive metrics

    return () => {
      unsubscribe();
      clearInterval(metricsInterval);
    };
  }, [isTracking, isPaused]);

  return {
    isTracking,
    isPaused,
    points,
    metrics,
    startTracking,
    pauseTracking,
    resumeTracking,
    stopTracking,
    exportGPX,
    exportFIT
  };
}
